import { Component, ViewChild, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { DaoAttendeesService } from 'src/app/providers/db/dao-attendees.service';
import { IonInfiniteScroll, MenuController, ModalController, NavController, Platform } from '@ionic/angular';
import { Events } from 'src/app/shared/services/global/events.service';
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { GlobalService, AnalyticsService } from 'src/app/shared/services';
import { TypeVisionModule } from '../../models/type-vision-module';
import { DaoGroupsService } from 'src/app/providers/db/dao-groups.service';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TypeTracking } from 'src/app/enums/type-tracking';
import { PathComponent } from 'src/app/models/path/path-components';
import { TranslateService } from '@ngx-translate/core';
import { EventColors } from 'src/app/models/event-colors';
import { UserDataService } from 'src/app/shared/services/user-data/user-data.service';
import { EventDataService } from 'src/app/shared/services/eventData/event-data.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

type ViewMode = 'list' | 'grid';

@Component({
    selector: 'app-attendees',
    templateUrl: './attendees.page.html',
    styleUrls: ['./attendees.page.scss']
})
export class AttendeesPage implements OnInit, OnDestroy {
    @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;
    @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

    subscriptions: Subscription[] = [];
    searchOpen: boolean = false;
    searchText: string = '';
    searchSubject = new Subject<string>();
    lastAttendees;
    attendees: Array<any> = [];
    loadingMoreAttendees: boolean = false;
    searchedAttendees: Array<any> = [];
    searchedAttendeesFixedArray: Array<any> = [];
    eventId: string = null;
    moduleId: string = null;
    loader: boolean = true;
    typeOrder: string;
    backBtn: boolean = true;
    allAttendeesList: Array<any> = [];
    eventColors: EventColors = new EventColors();
    menuBadge: number = 0;

    typeVisionModule = null;
    groups = [];
    userGroups: any = [];
    viewGroup: boolean = false;
    showNoResultImage = false;

    listCompany = [];
    listPost = [];
    filterBloc = true;
    isFilterOpen = true;
    allowFilterCompany = null;
    allowFilterPost = null;
    allowFilterCustomFields = null;
    showLetters: boolean = true;
    checkedCompanies = [];
    checkedPosts = [];
    checkedCustomFields: any[][] = [];
    isMobile: boolean = false;
    customFieldsTitle: any[] = [];
    customsFieldsArray: any[][] = [];
    customFieldsFomatTitle: any = [];
    allowFilterCustomArray: any = [];
    showOnlyLoggedUsers: any;
    sortOrder: string = 'asc';
    sortOptions: { value: string; label: string }[] = [];

    companyFilterText: string;
    postFilterText: string;
    moduleName: string;

    viewMode: ViewMode;
    currentPage: number = 1;
    pageSize: number = 24;

    constructor(
        private dbAttendees: DaoAttendeesService,
        private route: ActivatedRoute,
        private navCtrl: NavController,
        public toastController: ToastController,
        public global: GlobalService,
        private events: Events,
        private menuCtrl: MenuController,
        private SAnalytics: AnalyticsService,
        private dbGroups: DaoGroupsService,
        private translateService: TranslateService,
        private modalCtrl: ModalController,
        private SUserData: UserDataService,
        private SEventData: EventDataService,
        private platform: Platform,
    ) {
        this.menuCtrl.enable(true);

        const params = this.route.snapshot.params;
        this.eventId = params.eventId;
        this.moduleId = params.moduleId;

        this.loadColors();

        this.SAnalytics.moduleAccess(this.eventId, this.moduleId);
        this.SAnalytics.createTrackRoadToModule(this.eventId, this.moduleId, TypeTracking.ACCESS_TO_ATTENDEES_MODULE);

        this.startAll();
        this.menuBadge = this.global.notificationBadge;
        this.events.subscribe('menuBadge', () => {
            this.menuBadge = this.global.notificationBadge;
        });

        this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(value => {
            this.searchText = value;
            this.performSearch(value);
        });
    }

    get displayAttendees(): any[] {
        if (this.searchText || this.hasActiveFilters) {
            return this.searchedAttendees;
        }
        return this.attendees;
    }

    get hasActiveFilters(): boolean {
        return this.checkedCompanies.length > 0 ||
            this.checkedPosts.length > 0 ||
            this.hasCustomFieldFilters();
    }

    get paginatedAttendees(): any[] {
        const all = this.displayAttendees;
        const start = (this.currentPage - 1) * this.pageSize;
        return all.slice(start, start + this.pageSize);
    }

    get totalPages(): number {
        return Math.ceil(this.displayAttendees.length / this.pageSize) || 1;
    }

    get isLoadingMore(): boolean {
        return this.loadingMoreAttendees;
    }

    loadColors(eventId: string = this.eventId) {
        const subscription = this.SEventData.getEventDataListener(eventId).subscribe((eventData) => {
            this.eventColors = eventData.colors;
        });
        this.subscriptions.push(subscription);
    }

    ngOnInit() {
        if (this.platform.is('ios') || this.platform.is('android')) {
            this.isMobile = true;
        }
        this.viewMode = this.isMobile ? 'list' : 'grid';
        if (this.global.previousPage == 'container') {
            this.backBtn = false;
        } else {
            this.backBtn = true;
        }
        this.startAll();
    }

    ngOnDestroy() {
        this.viewGroup = false;
        this.dbAttendees.closeRefGetModule();
        this.dbGroups.closeRefContAttendeesOfGroup();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    trackByUid(index: number, item: any): string {
        return item?.uid || String(index);
    }

    toggleViewMode(mode: ViewMode) {
        this.viewMode = mode;
        this.currentPage = 1;
    }

    toggleFilter() {
        this.isFilterOpen = !this.isFilterOpen;
    }

    onSearchInput(ev: any) {
        const value = (ev?.target?.value || '').trim();
        this.searchSubject.next(value);
    }

    performSearch(value: string) {
        this.currentPage = 1;
        if (value.length >= 1) {
            const lowerValue = value.toLowerCase();
            const source = this.searchedAttendeesFixedArray.length > 0
                ? this.searchedAttendeesFixedArray
                : this.allAttendeesList;

            const filtered = source.filter(item => {
                const title = this.getAttendeePrincipalTitle(item.title);
                return (item.name && item.name.toLowerCase().includes(lowerValue)) ||
                    (title && title.toLowerCase().includes(lowerValue)) ||
                    (item.company && item.company.toLowerCase().includes(lowerValue));
            });
            this.searchedAttendees = this.getSortedCopy(filtered);
            this.showNoResultImage = this.searchedAttendees.length === 0;
        } else {
            this.searchedAttendees = this.searchedAttendeesFixedArray.length > 0
                ? this.getSortedCopy(this.searchedAttendeesFixedArray)
                : [];
            this.showNoResultImage = false;
        }
    }

    resitFilter() {
        this.searchedAttendees = this.searchedAttendeesFixedArray.length > 0
            ? this.getSortedCopy(this.searchedAttendeesFixedArray)
            : [];
        this.showNoResultImage = false;
        this.currentPage = 1;
    }

    clearFilters() {
        this.checkedCompanies = [];
        this.checkedPosts = [];
        for (const name of this.customFieldsTitle) {
            this.checkedCustomFields[name] = [];
        }
        this.searchedAttendeesFixedArray = [];
        this.searchedAttendees = [];
        this.showNoResultImage = false;
        this.currentPage = 1;
    }

    onListScroll() {
        if (!this.viewport || !this.lastAttendees || this.loadingMoreAttendees) return;
        const end = this.viewport.measureScrollOffset('bottom');
        if (end < 200) {
            this.moreAttendees(null);
        }
    }

    goToPage(direction: 'prev' | 'next') {
        if (direction === 'prev' && this.currentPage > 1) {
            this.currentPage--;
        } else if (direction === 'next' && this.currentPage < this.totalPages) {
            this.currentPage++;
            if (this.currentPage >= this.totalPages - 1 && this.lastAttendees) {
                this.moreAttendees(null);
            }
        }
    }

    setSortOrder(value: string) {
        if (this.sortOrder === value) return;
        this.sortOrder = value;
        this.currentPage = 1;

        if (this.allAttendeesList.length > 0) {
            this.allAttendeesList = this.getSortedCopy(this.allAttendeesList);
            this.attendees = [...this.allAttendeesList];
        } else {
            this.attendees = this.getSortedCopy(this.attendees);
        }
        this.lastAttendees = null;

        this.searchedAttendees = this.getSortedCopy(this.searchedAttendees);
        this.searchedAttendeesFixedArray = this.getSortedCopy(this.searchedAttendeesFixedArray);
    }

    private getSortComparator(): (a: any, b: any) => number {
        const multiplier = this.sortOrder === 'desc' ? -1 : 1;
        return (a, b) => {
            const nameA = a.queryName || a.name || '';
            const nameB = b.queryName || b.name || '';
            if (nameA < nameB) return -1 * multiplier;
            if (nameA > nameB) return 1 * multiplier;
            return 0;
        };
    }

    private getSortedCopy(array: any[]): any[] {
        return [...array].sort(this.getSortComparator());
    }

    startAll() {
        this.dbAttendees.getModule(this.moduleId, async (module) => {
            this.moduleName = module.name[this.SEventData.getLanguage()];
            this.allowFilterCompany = (module.allowFilterCompany == undefined) ?
                false : module.allowFilterCompany;
            this.allowFilterPost = (module.allowFilterPost == undefined) ?
                false : module.allowFilterPost;
            this.allowFilterCustomFields = (module.allowFilterCustomFields == undefined) ?
                false : module.allowFilterCustomFields;
            this.filterBloc = (
                module.allowFilterCompany === true ||
                module.allowFilterPost === true ||
                this.allowFilterCustomFields === true
            );
            this.isFilterOpen = this.isMobile ? false : this.filterBloc;
            this.showLetters = (module.showLetters === true);
            this.companyFilterText = (module.companyFilterText) ?
                module.companyFilterText :
                this.translateService.instant('pages.attendees.company');
            this.postFilterText = (module.postFilterText) ?
                module.postFilterText :
                this.translateService.instant('pages.attendees.post');
            this.showOnlyLoggedUsers = (module.showOnlyLoggedUsers == undefined) ?
                false : module.showOnlyLoggedUsers;

            this.dbAttendees.getFirstAttendeeFromModule(this.moduleId, () => {});

            this.typeOrder = module.orderUsers || 'asc';
            this.typeVisionModule = module.typeVision;
            this.sortOrder = this.typeOrder;
            this.sortOptions = [
                { value: 'asc', label: this.translateService.instant('pages.attendees.sort_az') },
                { value: 'desc', label: this.translateService.instant('pages.attendees.sort_za') },
            ];

            if (this.typeVisionModule === TypeVisionModule.GLOBAL_VISION) {
                this.viewGroup = false;
                this.getFirstAttendeesGlobalVision();
                this.getAttendeesAllGlobalVision();
            } else if (this.typeVisionModule === TypeVisionModule.DIVIDED_BY_GROUPS) {
                this.loader = true;
                this.viewGroup = true;
                this.getGroupsEvent();
            } else if (this.typeVisionModule === TypeVisionModule.GROUP_VISION) {
                this.loader = true;
                this.viewGroup = false;
                const userGroups: Object = (await this.SUserData.getUserDataSnapshot()).groups;
                const userGroupsIds: Array<string> = (userGroups) ? Object.keys(userGroups) : [];
                this.getAttendeesGroupVision(userGroupsIds);
            } else if (this.typeVisionModule === TypeVisionModule.GROUP_ACCESS_PERMISSION) {
                this.viewGroup = false;
                this.getFirstAttendeesLimitedAccessByGroup();
                this.getAttendeesAllLimitedAccessByGroup();
            }
        });
    }

    getGroupsEvent() {
        this.dbGroups.getGroups(this.eventId, (groups) => {
            this.groups = groups;

            groups.sort((a, b) => {
                return a.queryName < b.queryName ? -1 : a.queryName > b.queryName ? 1 : 0;
            });

            for (const group of groups) {
                const groupId = group.uid;
                const moduleAttendeeId = this.moduleId;
                group.qtdSessions = 0;

                this.dbGroups.contAttendeesOfGroup(groupId, moduleAttendeeId, (qtd: number) => {
                    group.qtdSessions = qtd;
                });
            }

            this.loader = false;
        });
    }

    async presentToast(time) {
        const toast = await this.toastController.create({
            message: time,
            duration: 3000
        });
        toast.present();
    }

    attendeeHeader() {
        const self = this;
        return function (attendee, i, attendees) {
            if (!self.showLetters) return null;
            if (i == 0) return attendee.letter;
            if (i != 0 && attendee.letter != attendees[i - 1].letter) return attendee.letter;
        };
    }

    moreAttendees(event) {
        if (this.typeVisionModule === TypeVisionModule.GLOBAL_VISION) {
            this.moreAttendeesGlobalVision(event);
        } else if (this.typeVisionModule === TypeVisionModule.GROUP_ACCESS_PERMISSION) {
            this.moreAttendeesAccessByGroup(event);
        } else {
            if (event?.target) event.target.disabled = true;
        }
    }

    profileDetails(attendee) {
        const navigationExtras: NavigationExtras = {
            state: { user: attendee }
        };
        this.navCtrl.navigateForward(
            `/event/${this.eventId}/profile/${this.moduleId}/${attendee.type}/${attendee.uid}`,
            navigationExtras
        );
        this.global.previousPage = null;
    }

    getFirstAttendeesGlobalVision() {
        this.dbAttendees.getFirstAttendeesGlobalVision(this.typeOrder, this.moduleId, (data) => {
            if (data.length > 0) {
                this.lastAttendees = data[data.length - 1];
            }

            const filteredList = data.filter(element => !this.showOnlyLoggedUsers || element.firstAccess === true);
            filteredList.forEach(element => {
                element.letter = (element.name[0] || '').toUpperCase();
                element.principal_title = this.getAttendeePrincipalTitle(element.title);
            });

            this.attendees = filteredList;
            this.buildFilterLists(filteredList);
            this.loader = false;
        });
    }

    getAttendeesAllGlobalVision() {
        this.subscriptions.push(this.dbAttendees.getAttendeesAllGlobalVision(this.moduleId).subscribe((data) => {
            this.allAttendeesList = data;
            this.buildFilterLists(data);
        }));
    }

    private buildFilterLists(attendees: any[]) {
        let companies: string[] = [], posts: string[] = [], customs: any[][] = [];

        attendees.forEach(element => {
            element.principal_title = this.getAttendeePrincipalTitle(element.title);
            if (element.company && element.company !== '') companies.push(element.company);
            if (element.principal_title !== '') posts.push(element.principal_title);

            const customTemp = [];
            if (element.customFields) {
                for (const cus of element.customFields) {
                    if (cus.exibicao === '1') {
                        customTemp.push(cus.textValue[this.SEventData.getLanguage()]);
                    }
                }
                customs.push(customTemp);
            }
        });

        companies = [...companies].sort((a, b) =>
            a.toLowerCase() > b.toLowerCase() ? 1 : a.toLowerCase() < b.toLowerCase() ? -1 : 0
        );
        posts = [...posts].sort((a, b) =>
            a.toLowerCase() > b.toLowerCase() ? 1 : a.toLowerCase() < b.toLowerCase() ? -1 : 0
        );

        if (attendees.some(x => x.company === null || x.company === '')) {
            companies.push(this.translateService.instant('pages.attendees.not_specified'));
        }
        if (attendees.some(x => x.principal_title === '')) {
            posts.push(this.translateService.instant('pages.attendees.not_specified'));
        }

        this.listCompany = Array.from(new Set([...companies]));
        this.listPost = Array.from(new Set([...posts]));

        const size = customs.length;
        for (let i = 0; i < size; i++) {
            this.customsFieldsArray[i] = Array.from(new Set([...customs[i]]));
        }

        const tempArray = [];
        for (let i = 0; i < customs.length; i++) {
            let tempp = customs[i];
            for (let j = (i + 1); j < customs.length; j++) {
                tempp.map(val => {
                    for (let id = 0; id < customs[j].length; id++) {
                        if (val.trim().toLowerCase() === customs[j][id].trim().toLowerCase()) {
                            customs[j][id] = '';
                        }
                    }
                });
                tempArray.push(customs[j]);
            }
            tempArray.push(tempp);
        }
        for (let i = 1; i < tempArray.length; i++) {
            if (tempArray[i].length === 0) {
                tempArray[i] = tempArray[tempArray.length - 1];
            }
        }
        this.customsFieldsArray = tempArray;
    }

    moreAttendeesGlobalVision(event) {
        if (this.loadingMoreAttendees) return;
        this.loadingMoreAttendees = true;

        this.dbAttendees.getNextPageAttendeesGlobalVision(
            this.moduleId,
            this.lastAttendees,
            this.sortOrder,
            (data) => {
                if (data.length > 0) {
                    this.lastAttendees = data[data.length - 1];
                } else {
                    this.lastAttendees = null;
                }

                const newData = data
                    .map(element => {
                        element.letter = (element.name[0] || '').toUpperCase();
                        element.principal_title = this.getAttendeePrincipalTitle(element.title);
                        return element;
                    })
                    .filter(element => !this.showOnlyLoggedUsers || element.firstAccess === true);

                this.attendees = [...this.attendees, ...newData];
                this.attendees = this.getSortedCopy(this.attendees);
                this.loadingMoreAttendees = false;

                if (event?.target) {
                    event.target.complete();
                    if (!this.lastAttendees || data.length === 0) {
                        event.target.disabled = true;
                    }
                }
            }
        );
    }

    getAttendeesGroupDividByGroup(group) {
        const groupId = group.uid;
        this.loader = true;
        this.viewGroup = false;

        this.dbAttendees.getAttendeesGroupGlobalVision(groupId, this.typeOrder, this.moduleId, (data) => {
            this.loader = false;
            this.lastAttendees = null;
            this.attendees = [];
            this.allAttendeesList = [];

            if (data.length > 0) {
                this.lastAttendees = data[data.length - 1];
            }

            const filteredList = data.filter(element => !this.showOnlyLoggedUsers || element.firstAccess === true);
            filteredList.forEach(element => {
                element.letter = (element.name[0] || '').toUpperCase();
                element.principal_title = this.getAttendeePrincipalTitle(element.title);
            });

            this.attendees = filteredList;
            this.allAttendeesList = filteredList;
        });
    }

    getAttendeesGroupVision(userGroupsIds: string[]) {
        this.dbAttendees.getAttendeesGroupsVision(userGroupsIds, this.typeOrder, this.moduleId, (data) => {
            this.loader = false;
            this.lastAttendees = null;
            this.attendees = [];
            this.allAttendeesList = [];

            if (data.length > 0) {
                this.lastAttendees = data[data.length - 1];
            }

            data.forEach(element => {
                const letter = element.name.charAt(0).toUpperCase();
                element.letter = letter;
                element.principal_title = this.getAttendeePrincipalTitle(element.title);
            });

            this.attendees = data;
            this.allAttendeesList = data;
        });
    }

    getFirstAttendeesLimitedAccessByGroup() {
        this.dbAttendees.getFirstAttendeesGlobalVision(this.typeOrder, this.moduleId, (data) => {
            this.allAttendeesList = [];

            if (data.length > 0) {
                this.lastAttendees = data[data.length - 1];
            }

            const filteredList = data.filter(element => !this.showOnlyLoggedUsers || element.firstAccess === true);
            filteredList.forEach(element => {
                element.letter = (element.name[0] || '').toUpperCase();
                element.principal_title = this.getAttendeePrincipalTitle(element.title);
            });

            this.attendees = filteredList;
            this.loader = false;
        });
    }

    getAttendeesAllLimitedAccessByGroup() {
        this.subscriptions.push(this.dbAttendees.getAttendeesAllLimitedAccessByGroup(this.moduleId).subscribe((data) => {
            this.allAttendeesList = data;
        }));
    }

    moreAttendeesAccessByGroup(event) {
        if (this.lastAttendees) {
            this.dbAttendees.getNextPageAttendeesLimitedAccessByGroup(
                this.moduleId,
                this.lastAttendees,
                this.typeOrder,
                (data) => {
                    this.lastAttendees = null;
                    if (data.length > 0) {
                        this.lastAttendees = data[data.length - 1];
                    }

                    data.forEach(element => {
                        const letter = element.name.charAt(0).toUpperCase();
                        element.letter = letter;
                        element.principal_title = this.getAttendeePrincipalTitle(element.title);
                        if (!this.showOnlyLoggedUsers || element.firstAccess === true) {
                            this.attendees.push(element);
                        }
                    });

                    if (this.lastAttendees) {
                        if (event?.target) event.target.complete();
                    } else {
                        if (event?.target) event.target.disabled = true;
                    }
                }
            );
        } else {
            if (event?.target) event.target.disabled = true;
        }
    }

    getAttendeePrincipalTitle(title) {
        return title ? title[this.SEventData.getLanguage()] : '';
    }

    hasCustomFieldFilters(): boolean {
        for (const name of this.customFieldsTitle) {
            if (this.checkedCustomFields[name] && this.checkedCustomFields[name].length > 0) {
                return true;
            }
        }
        return false;
    }

    async showFilterBloc() {
        if (this.isMobile) {
            const list = [];
            if (this.allowFilterCompany) {
                list.push({ key: 'company', value: this.listCompany });
            }
            if (this.allowFilterPost) {
                list.push({ key: 'post', value: this.listPost });
            }
            const modal = await this.modalCtrl.create({
                component: PathComponent.filter_by,
                componentProps: {
                    mode: 'mobile',
                    comp: 'attendees',
                    choices: list,
                    selectedValues: [
                        { key: 'company', value: this.checkedCompanies },
                        { key: 'post', value: this.checkedPosts }
                    ],
                    colors: {
                        'link_color': this.eventColors.link_color,
                        'title_color': this.eventColors.title_color,
                        'btn_text_color': this.eventColors.btn_text_color
                    },
                    companyFilterText: this.companyFilterText,
                    postFilterText: this.postFilterText
                },
                cssClass: 'modal-order-by'
            });

            modal.onDidDismiss().then(ret => {
                this.checkedCompanies = ret.data.selectedValues.filter(x => x.key === 'company')[0].value;
                this.checkedPosts = ret.data.selectedValues.filter(x => x.key === 'post')[0].value;
                this.filterArray(this.checkedPosts, this.checkedCompanies);
            });
            return await modal.present();
        } else {
            this.isFilterOpen = !this.isFilterOpen;
        }
    }

    async showFilterBlocCustoms() {
        if (this.isMobile) {
            const list = [];
            if (this.allowFilterCompany) {
                list.push({ key: 'company', value: this.listCompany });
            }
            if (this.allowFilterPost) {
                list.push({ key: 'post', value: this.listPost });
            }
            if (this.allowFilterCustomFields) {
                list.push({ key: 'customs', value: this.getCustomFilterList() });
            }

            const modal = await this.modalCtrl.create({
                component: PathComponent.filter_by,
                componentProps: {
                    mode: 'mobile',
                    comp: 'attendees',
                    choices: list,
                    selectedValues: [
                        { key: 'customs', value: this.checkedCustomFields },
                        { key: 'company', value: this.checkedCompanies },
                        { key: 'post', value: this.checkedPosts }
                    ],
                    colors: {
                        'link_color': this.eventColors.link_color,
                        'title_color': this.eventColors.title_color,
                        'btn_text_color': this.eventColors.btn_text_color
                    },
                    companyFilterText: this.companyFilterText,
                    postFilterText: this.postFilterText
                },
                cssClass: 'modal-order-by'
            });

            modal.onDidDismiss().then(ret => {
                this.checkedCustomFields = ret.data.selectedValues.filter(x => x.key === 'company')[0].value;
                this.checkedPosts = ret.data.selectedValues.filter(x => x.key === 'post')[0].value;
                this.checkedCustomFields = ret.data.selectedValues.filter(x => x.key === 'customs')[0].value;

                let customFieldActive = false;
                for (const name of this.customFieldsTitle) {
                    if (this.checkedCustomFields[name] && this.checkedCustomFields[name].length > 0) {
                        customFieldActive = true;
                    }
                }
                if (customFieldActive) {
                    this.filterArrayWithCustomFields(this.checkedPosts, this.checkedCompanies, this.checkedCustomFields);
                } else {
                    this.filterArray(this.checkedPosts, this.checkedCompanies);
                }
            });
            return await modal.present();
        } else {
            this.isFilterOpen = !this.isFilterOpen;
        }
    }

    checkedChanged(ev, type: string) {
        const value = ev.target.value;
        const isChecked = ev.target.checked;
        if (type === 'company') {
            this.checkedCompanies = isChecked
                ? [...this.checkedCompanies, value]
                : this.checkedCompanies.filter(x => x !== value);
            this.checkedCompanies = Array.from(new Set(this.checkedCompanies));
        } else if (type === 'post') {
            this.checkedPosts = isChecked
                ? [...this.checkedPosts, value]
                : this.checkedPosts.filter(x => x !== value);
            this.checkedPosts = Array.from(new Set(this.checkedPosts));
        } else {
            if (!this.checkedCustomFields[type]) this.checkedCustomFields[type] = [];
            this.checkedCustomFields[type] = isChecked
                ? [...this.checkedCustomFields[type], value]
                : this.checkedCustomFields[type].filter(x => x !== value);
        }

        this.currentPage = 1;
        let customFieldActive = false;
        for (const name of this.customFieldsTitle) {
            if (this.checkedCustomFields[name] && this.checkedCustomFields[name].length > 0) {
                customFieldActive = true;
            }
        }
        if (customFieldActive) {
            this.filterArrayWithCustomFields(this.checkedPosts, this.checkedCompanies, this.checkedCustomFields);
        } else {
            this.filterArray(this.checkedPosts, this.checkedCompanies);
        }
    }

    filterArray(posts: Array<any>, companies: Array<any>) {
        const not_specified = this.translateService.instant('pages.attendees.not_specified');
        this.currentPage = 1;

        if (posts.length === 0 && companies.length > 0) {
            this.searchedAttendees = this.getSortedCopy([...this.allAttendeesList].filter(item => {
                item.principal_title = this.getAttendeePrincipalTitle(item.title);
                return ((item.company === null || item.company === '') && companies.includes(not_specified) || companies.includes(item.company)) && item;
            }));
            this.searchedAttendeesFixedArray = [...this.searchedAttendees];
            return;
        }
        if (posts.length > 0 && companies.length === 0) {
            this.searchedAttendees = this.getSortedCopy([...this.allAttendeesList].filter(item => {
                item.principal_title = this.getAttendeePrincipalTitle(item.title);
                return ((item.principal_title === null || item.principal_title === '') && posts.includes(not_specified) || posts.includes(item.principal_title)) && item;
            }));
            this.searchedAttendeesFixedArray = [...this.searchedAttendees];
            return;
        }
        if (posts.length > 0 && companies.length > 0) {
            this.searchedAttendees = this.getSortedCopy([...this.allAttendeesList].filter(item => {
                item.principal_title = this.getAttendeePrincipalTitle(item.title);
                return (companies.includes(not_specified) && posts.includes(not_specified) && (item.company === null || item.company === '') && (item.principal_title === null || item.principal_title === '') || posts.includes(item.principal_title) && companies.includes(item.company)) && item;
            }));
            if (this.searchedAttendees.length === 0) this.showNoResultImage = true;
            this.searchedAttendeesFixedArray = [...this.searchedAttendees];
            return;
        }
        if (posts.length === 0 && companies.length === 0) {
            this.searchedAttendees = [];
            this.searchedAttendeesFixedArray = [];
            this.showNoResultImage = false;
        }
    }

    filterArrayWithCustomFields(posts: Array<any>, companies: Array<any>, customFields?: Array<any>) {
        const not_specified = this.translateService.instant('pages.attendees.not_specified');
        this.currentPage = 1;

        for (const title of this.customFieldsTitle) {
            if (customFields[title] && customFields[title].length > 0) {
                this.searchedAttendees = this.getSortedCopy([...this.allAttendeesList].filter(item => {
                    item.principal_title = this.getAttendeePrincipalTitle(item.title);
                    for (const cusTitle of this.customFieldsTitle) {
                        if (customFields[cusTitle] && item.customFields) {
                            for (let i = 0; i < item.customFields.length; i++) {
                                if (posts.length === 0 && companies.length > 0) {
                                    if (companies.includes(not_specified) && (item.company === null || item.company === '') && (item.customFields[i].textValue[this.SEventData.getLanguage()] === '' || item.customFields[i].textValue[this.SEventData.getLanguage()] === null) ||
                                        companies.includes(item.company) && customFields[cusTitle].includes(item.customFields[i].textValue[this.SEventData.getLanguage()]))
                                        return item;
                                }
                                if (posts.length > 0 && companies.length === 0) {
                                    if (posts.includes(not_specified) && (item.principal_title === null || item.principal_title === '') && (item.customFields[i].textValue[this.SEventData.getLanguage()] === '' || item.customFields[i].textValue[this.SEventData.getLanguage()] === null) ||
                                        posts.includes(item.principal_title) && customFields[cusTitle].includes(item.customFields[i].textValue[this.SEventData.getLanguage()]))
                                        return item;
                                }
                                if (posts.length > 0 && companies.length > 0) {
                                    if (companies.includes(not_specified) && posts.includes(not_specified) && (item.company === null || item.company === '') && (item.principal_title === null || item.principal_title === '') && (item.customFields[i].textValue[this.SEventData.getLanguage()] === '' || item.customFields[i].textValue[this.SEventData.getLanguage()] === null) ||
                                        companies.includes(item.company) && posts.includes(item.principal_title) && customFields[cusTitle].includes(item.customFields[i].textValue[this.SEventData.getLanguage()]))
                                        return item;
                                }
                                if (posts.length === 0 && companies.length === 0) {
                                    if ((companies.includes(not_specified) && posts.includes(not_specified) && (item.company === null || item.company === '') && (item.principal_title === null || item.principal_title === '')) ||
                                        customFields[cusTitle].includes(item.customFields[i].textValue[this.SEventData.getLanguage()]))
                                        return item;
                                }
                            }
                        }
                    }
                    return false;
                }));
                if (this.searchedAttendees.length === 0) this.showNoResultImage = true;
                this.searchedAttendeesFixedArray = [...this.searchedAttendees];
                return;
            }
        }
    }

    removeItem(arrType, item?: any) {
        switch (arrType) {
            case 'all':
                this.checkedPosts = [];
                this.checkedCompanies = [];
                for (let i = 0; i < this.customFieldsTitle.length; i++) {
                    this.checkedCustomFields[this.customFieldsTitle[i]] = [];
                }
                break;
            case 'post':
                if (item !== undefined && item !== null) {
                    this.checkedPosts = this.checkedPosts.filter(p => p !== item);
                } else {
                    this.checkedPosts = [];
                }
                break;
            case 'company':
                if (item !== undefined && item !== null) {
                    this.checkedCompanies = this.checkedCompanies.filter(p => p !== item);
                } else {
                    this.checkedCompanies = [];
                }
                break;
            default:
                if (this.customFieldsTitle.includes(arrType)) {
                    const index = this.customFieldsTitle.indexOf(arrType);
                    const elt = this.customFieldsTitle[index];
                    if (this.checkedCustomFields[elt]) {
                        this.checkedCustomFields[elt] = item !== undefined
                            ? this.checkedCustomFields[elt].filter(p => p !== item)
                            : [];
                    }
                }
        }

        this.currentPage = 1;
        let customFieldActive = false;
        for (const name of this.customFieldsTitle) {
            if (this.checkedCustomFields[name] && this.checkedCustomFields[name].length > 0) {
                customFieldActive = true;
            }
        }
        if (customFieldActive) {
            this.filterArrayWithCustomFields(this.checkedPosts, this.checkedCompanies, this.checkedCustomFields);
        } else {
            this.filterArray(this.checkedPosts, this.checkedCompanies);
        }
        if (this.searchedAttendees.length === 0) this.showNoResultImage = false;
    }

    getCustomFilterList(): any[][] {
        const list = [];
        if (this.customFieldsTitle.length > 0) {
            return this.customsFieldsArray;
        }
        return list;
    }

    getDoubleArraySize(array: [][]): number {
        let i = 0;
        for (i = 0; i < array.length[0]; i++);
        return i;
    }

    verifEmptyArrays(ch: any) {
        return this.checkedCustomFields.includes(ch);
    }
}
